import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  FundingApplication,
  FundingApplicationFormData,
  FundingApplicationStatus,
  FundingProgram,
} from '@/models/funding-application.model';

const COLLECTION_NAME = 'fundingApplications';

export class FundingApplicationService {
  /**
   * Convert Firestore timestamp to Date
   */
  private static toDate(value: Date | Timestamp | undefined): Date | undefined {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    return value.toDate();
  }

  /**
   * Convert Date to Firestore Timestamp
   */
  private static toTimestamp(value: Date | undefined): Timestamp | undefined {
    if (!value) return undefined;
    return Timestamp.fromDate(value);
  }

  /**
   * Convert Firestore document to FundingApplication
   */
  private static fromFirestore(doc: any): FundingApplication {
    const data = doc.data();
    return {
      id: doc.id,
      program: data.program,
      programName: data.programName || '',
      fundingType: data.fundingType,
      status: data.status,
      applicationDate: this.toDate(data.applicationDate) || new Date(),
      submissionDate: this.toDate(data.submissionDate),
      decisionDate: this.toDate(data.decisionDate),
      fundingDate: this.toDate(data.fundingDate),
      requestedAmount: data.requestedAmount || 0,
      approvedAmount: data.approvedAmount,
      fundedAmount: data.fundedAmount,
      useOfFunds: data.useOfFunds || '',
      monthlyBurn: data.monthlyBurn,
      cloudAiSubscriptions: data.cloudAiSubscriptions,
      runwayMonths: data.runwayMonths,
      earliestRevenueTrigger: data.earliestRevenueTrigger,
      operatingState: data.operatingState || 'Minnesota',
      businessType: data.businessType || '',
      incorporationState: data.incorporationState || 'Delaware',
      foreignQualificationStatus: data.foreignQualificationStatus,
      ein: data.ein,
      dunsNumber: data.dunsNumber,
      yearsInBusiness: data.yearsInBusiness,
      legalStructure: data.legalStructure,
      businessAddress: data.businessAddress,
      businessPhone: data.businessPhone,
      businessWebsite: data.businessWebsite,
      currentRevenue: data.currentRevenue,
      projectedRevenue: data.projectedRevenue,
      currentExpenses: data.currentExpenses,
      bankAccountBalance: data.bankAccountBalance,
      existingDebt: data.existingDebt,
      monthlyDebtPayments: data.monthlyDebtPayments,
      creditScore: data.creditScore,
      taxReturnsFiled: data.taxReturnsFiled,
      yearsTaxReturnsFiled: data.yearsTaxReturnsFiled,
      founderName: data.founderName,
      founderTitle: data.founderTitle,
      founderOwnership: data.founderOwnership,
      founderExperience: data.founderExperience,
      personalCreditScore: data.personalCreditScore,
      personalFinancialStatement: data.personalFinancialStatement,
      executiveSummary: data.executiveSummary,
      marketAnalysis: data.marketAnalysis,
      competitiveAdvantage: data.competitiveAdvantage,
      businessModel: data.businessModel,
      targetCustomers: data.targetCustomers,
      salesStrategy: data.salesStrategy,
      managementTeam: data.managementTeam,
      numberOfEmployees: data.numberOfEmployees,
      monthlyPayroll: data.monthlyPayroll,
      keyEmployees: data.keyEmployees,
      collateralDescription: data.collateralDescription,
      assetsValue: data.assetsValue,
      equipmentValue: data.equipmentValue,
      repaymentPlan: data.repaymentPlan,
      repaymentAbility: data.repaymentAbility,
      revenueProjections: data.revenueProjections,
      references: data.references,
      businessLicenses: data.businessLicenses,
      insuranceInfo: data.insuranceInfo,
      bankReferences: data.bankReferences,
      applicationNumber: data.applicationNumber,
      lenderContactName: data.lenderContactName,
      lenderContactEmail: data.lenderContactEmail,
      lenderContactPhone: data.lenderContactPhone,
      lenderApplicationUrl: data.lenderApplicationUrl,
      documents: data.documents || [],
      requiredDocuments: data.requiredDocuments || [],
      documentsSubmitted: data.documentsSubmitted || [],
      notes: data.notes || '',
      lenderNotes: data.lenderNotes,
      applicationFormData: data.applicationFormData,
      expectedDecisionDate: this.toDate(data.expectedDecisionDate),
      nextFollowUpDate: this.toDate(data.nextFollowUpDate),
      createdBy: data.createdBy,
      createdAt: this.toDate(data.createdAt) || new Date(),
      updatedAt: this.toDate(data.updatedAt) || new Date(),
      updatedBy: data.updatedBy,
    };
  }

  /**
   * Convert FundingApplication to Firestore document
   */
  private static toFirestore(application: FundingApplication): any {
    return {
      program: application.program,
      programName: application.programName || '',
      fundingType: application.fundingType,
      status: application.status,
      applicationDate: this.toTimestamp(application.applicationDate as Date),
      submissionDate: this.toTimestamp(application.submissionDate as Date),
      decisionDate: this.toTimestamp(application.decisionDate as Date),
      fundingDate: this.toTimestamp(application.fundingDate as Date),
      requestedAmount: application.requestedAmount,
      approvedAmount: application.approvedAmount,
      fundedAmount: application.fundedAmount,
      useOfFunds: application.useOfFunds,
      monthlyBurn: application.monthlyBurn,
      cloudAiSubscriptions: application.cloudAiSubscriptions,
      runwayMonths: application.runwayMonths,
      earliestRevenueTrigger: application.earliestRevenueTrigger,
      operatingState: application.operatingState,
      businessType: application.businessType,
      incorporationState: application.incorporationState,
      foreignQualificationStatus: application.foreignQualificationStatus,
      ein: application.ein,
      dunsNumber: application.dunsNumber,
      yearsInBusiness: application.yearsInBusiness,
      legalStructure: application.legalStructure,
      businessAddress: application.businessAddress,
      businessPhone: application.businessPhone,
      businessWebsite: application.businessWebsite,
      currentRevenue: application.currentRevenue,
      projectedRevenue: application.projectedRevenue,
      currentExpenses: application.currentExpenses,
      bankAccountBalance: application.bankAccountBalance,
      existingDebt: application.existingDebt,
      monthlyDebtPayments: application.monthlyDebtPayments,
      creditScore: application.creditScore,
      taxReturnsFiled: application.taxReturnsFiled,
      yearsTaxReturnsFiled: application.yearsTaxReturnsFiled,
      founderName: application.founderName,
      founderTitle: application.founderTitle,
      founderOwnership: application.founderOwnership,
      founderExperience: application.founderExperience,
      personalCreditScore: application.personalCreditScore,
      personalFinancialStatement: application.personalFinancialStatement,
      executiveSummary: application.executiveSummary,
      marketAnalysis: application.marketAnalysis,
      competitiveAdvantage: application.competitiveAdvantage,
      businessModel: application.businessModel,
      targetCustomers: application.targetCustomers,
      salesStrategy: application.salesStrategy,
      managementTeam: application.managementTeam,
      numberOfEmployees: application.numberOfEmployees,
      monthlyPayroll: application.monthlyPayroll,
      keyEmployees: application.keyEmployees,
      collateralDescription: application.collateralDescription,
      assetsValue: application.assetsValue,
      equipmentValue: application.equipmentValue,
      repaymentPlan: application.repaymentPlan,
      repaymentAbility: application.repaymentAbility,
      revenueProjections: application.revenueProjections,
      references: application.references,
      businessLicenses: application.businessLicenses,
      insuranceInfo: application.insuranceInfo,
      bankReferences: application.bankReferences,
      applicationNumber: application.applicationNumber,
      lenderContactName: application.lenderContactName,
      lenderContactEmail: application.lenderContactEmail,
      lenderContactPhone: application.lenderContactPhone,
      lenderApplicationUrl: application.lenderApplicationUrl,
      documents: application.documents || [],
      requiredDocuments: application.requiredDocuments || [],
      documentsSubmitted: application.documentsSubmitted || [],
      notes: application.notes || '',
      lenderNotes: application.lenderNotes,
      applicationFormData: application.applicationFormData,
      expectedDecisionDate: this.toTimestamp(application.expectedDecisionDate as Date),
      nextFollowUpDate: this.toTimestamp(application.nextFollowUpDate as Date),
      createdBy: application.createdBy,
      createdAt: this.toTimestamp(application.createdAt as Date) || Timestamp.now(),
      updatedAt: Timestamp.now(),
      updatedBy: application.updatedBy,
    };
  }

  /**
   * Get all funding applications
   */
  static async getAll(
    filters?: {
      status?: FundingApplicationStatus;
      program?: FundingProgram;
    }
  ): Promise<FundingApplication[]> {
    const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')];

    if (filters?.status) {
      constraints.push(where('status', '==', filters.status));
    }

    if (filters?.program) {
      constraints.push(where('program', '==', filters.program));
    }

    const q = query(collection(db, COLLECTION_NAME), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => this.fromFirestore(doc));
  }

  /**
   * Get a single funding application by ID
   */
  static async getById(id: string): Promise<FundingApplication | null> {
    const docRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return this.fromFirestore(docSnap);
  }

  /**
   * Create a new funding application
   */
  static async create(
    formData: FundingApplicationFormData,
    userId: string
  ): Promise<string> {
    const application: FundingApplication = {
      program: formData.program,
      programName: formData.programName || '',
      fundingType: formData.fundingType,
      status: FundingApplicationStatus.DRAFT,
      applicationDate: new Date(),
      requestedAmount: formData.requestedAmount,
      useOfFunds: formData.useOfFunds,
      monthlyBurn: formData.monthlyBurn,
      cloudAiSubscriptions: formData.cloudAiSubscriptions,
      runwayMonths: formData.runwayMonths,
      earliestRevenueTrigger: formData.earliestRevenueTrigger,
      operatingState: formData.operatingState || 'Minnesota',
      businessType: formData.businessType || '',
      incorporationState: formData.incorporationState || 'Delaware',
      foreignQualificationStatus: formData.foreignQualificationStatus,
      ein: formData.ein,
      dunsNumber: formData.dunsNumber,
      yearsInBusiness: formData.yearsInBusiness,
      legalStructure: formData.legalStructure,
      businessAddress: formData.businessAddress,
      businessPhone: formData.businessPhone,
      businessWebsite: formData.businessWebsite,
      currentRevenue: formData.currentRevenue,
      projectedRevenue: formData.projectedRevenue,
      currentExpenses: formData.currentExpenses,
      bankAccountBalance: formData.bankAccountBalance,
      existingDebt: formData.existingDebt,
      monthlyDebtPayments: formData.monthlyDebtPayments,
      creditScore: formData.creditScore,
      taxReturnsFiled: formData.taxReturnsFiled,
      yearsTaxReturnsFiled: formData.yearsTaxReturnsFiled,
      founderName: formData.founderName,
      founderTitle: formData.founderTitle,
      founderOwnership: formData.founderOwnership,
      founderExperience: formData.founderExperience,
      personalCreditScore: formData.personalCreditScore,
      personalFinancialStatement: formData.personalFinancialStatement,
      executiveSummary: formData.executiveSummary,
      marketAnalysis: formData.marketAnalysis,
      competitiveAdvantage: formData.competitiveAdvantage,
      businessModel: formData.businessModel,
      targetCustomers: formData.targetCustomers,
      salesStrategy: formData.salesStrategy,
      managementTeam: formData.managementTeam,
      numberOfEmployees: formData.numberOfEmployees,
      monthlyPayroll: formData.monthlyPayroll,
      keyEmployees: formData.keyEmployees,
      collateralDescription: formData.collateralDescription,
      assetsValue: formData.assetsValue,
      equipmentValue: formData.equipmentValue,
      repaymentPlan: formData.repaymentPlan,
      repaymentAbility: formData.repaymentAbility,
      revenueProjections: formData.revenueProjections,
      references: formData.references,
      businessLicenses: formData.businessLicenses,
      insuranceInfo: formData.insuranceInfo,
      bankReferences: formData.bankReferences,
      applicationNumber: formData.applicationNumber,
      lenderContactName: formData.lenderContactName,
      lenderContactEmail: formData.lenderContactEmail,
      lenderContactPhone: formData.lenderContactPhone,
      lenderApplicationUrl: formData.lenderApplicationUrl,
      documents: [],
      requiredDocuments: formData.requiredDocuments || [],
      documentsSubmitted: formData.documentsSubmitted || [],
      notes: formData.notes || '',
      expectedDecisionDate: formData.expectedDecisionDate,
      nextFollowUpDate: formData.nextFollowUpDate,
      createdBy: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), this.toFirestore(application));
    return docRef.id;
  }

  /**
   * Update a funding application
   */
  static async update(
    id: string,
    updates: Partial<FundingApplicationFormData>,
    userId: string
  ): Promise<void> {
    const docRef = doc(db, COLLECTION_NAME, id);
    const updateData: any = {
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    };

    // Only update fields that are provided
    if (updates.program !== undefined) updateData.program = updates.program;
    if (updates.programName !== undefined) updateData.programName = updates.programName;
    if (updates.fundingType !== undefined) updateData.fundingType = updates.fundingType;
    if (updates.requestedAmount !== undefined) updateData.requestedAmount = updates.requestedAmount;
    if (updates.useOfFunds !== undefined) updateData.useOfFunds = updates.useOfFunds;
    if (updates.monthlyBurn !== undefined) updateData.monthlyBurn = updates.monthlyBurn;
    if (updates.cloudAiSubscriptions !== undefined) updateData.cloudAiSubscriptions = updates.cloudAiSubscriptions;
    if (updates.runwayMonths !== undefined) updateData.runwayMonths = updates.runwayMonths;
    if (updates.earliestRevenueTrigger !== undefined) updateData.earliestRevenueTrigger = updates.earliestRevenueTrigger;
    if (updates.operatingState !== undefined) updateData.operatingState = updates.operatingState;
    if (updates.businessType !== undefined) updateData.businessType = updates.businessType;
    if (updates.incorporationState !== undefined) updateData.incorporationState = updates.incorporationState;
    if (updates.foreignQualificationStatus !== undefined) updateData.foreignQualificationStatus = updates.foreignQualificationStatus;
    if (updates.ein !== undefined) updateData.ein = updates.ein;
    if (updates.dunsNumber !== undefined) updateData.dunsNumber = updates.dunsNumber;
    if (updates.yearsInBusiness !== undefined) updateData.yearsInBusiness = updates.yearsInBusiness;
    if (updates.legalStructure !== undefined) updateData.legalStructure = updates.legalStructure;
    if (updates.businessAddress !== undefined) updateData.businessAddress = updates.businessAddress;
    if (updates.businessPhone !== undefined) updateData.businessPhone = updates.businessPhone;
    if (updates.businessWebsite !== undefined) updateData.businessWebsite = updates.businessWebsite;
    if (updates.currentRevenue !== undefined) updateData.currentRevenue = updates.currentRevenue;
    if (updates.projectedRevenue !== undefined) updateData.projectedRevenue = updates.projectedRevenue;
    if (updates.currentExpenses !== undefined) updateData.currentExpenses = updates.currentExpenses;
    if (updates.bankAccountBalance !== undefined) updateData.bankAccountBalance = updates.bankAccountBalance;
    if (updates.existingDebt !== undefined) updateData.existingDebt = updates.existingDebt;
    if (updates.monthlyDebtPayments !== undefined) updateData.monthlyDebtPayments = updates.monthlyDebtPayments;
    if (updates.creditScore !== undefined) updateData.creditScore = updates.creditScore;
    if (updates.taxReturnsFiled !== undefined) updateData.taxReturnsFiled = updates.taxReturnsFiled;
    if (updates.yearsTaxReturnsFiled !== undefined) updateData.yearsTaxReturnsFiled = updates.yearsTaxReturnsFiled;
    if (updates.founderName !== undefined) updateData.founderName = updates.founderName;
    if (updates.founderTitle !== undefined) updateData.founderTitle = updates.founderTitle;
    if (updates.founderOwnership !== undefined) updateData.founderOwnership = updates.founderOwnership;
    if (updates.founderExperience !== undefined) updateData.founderExperience = updates.founderExperience;
    if (updates.personalCreditScore !== undefined) updateData.personalCreditScore = updates.personalCreditScore;
    if (updates.personalFinancialStatement !== undefined) updateData.personalFinancialStatement = updates.personalFinancialStatement;
    if (updates.executiveSummary !== undefined) updateData.executiveSummary = updates.executiveSummary;
    if (updates.marketAnalysis !== undefined) updateData.marketAnalysis = updates.marketAnalysis;
    if (updates.competitiveAdvantage !== undefined) updateData.competitiveAdvantage = updates.competitiveAdvantage;
    if (updates.businessModel !== undefined) updateData.businessModel = updates.businessModel;
    if (updates.targetCustomers !== undefined) updateData.targetCustomers = updates.targetCustomers;
    if (updates.salesStrategy !== undefined) updateData.salesStrategy = updates.salesStrategy;
    if (updates.managementTeam !== undefined) updateData.managementTeam = updates.managementTeam;
    if (updates.numberOfEmployees !== undefined) updateData.numberOfEmployees = updates.numberOfEmployees;
    if (updates.monthlyPayroll !== undefined) updateData.monthlyPayroll = updates.monthlyPayroll;
    if (updates.keyEmployees !== undefined) updateData.keyEmployees = updates.keyEmployees;
    if (updates.collateralDescription !== undefined) updateData.collateralDescription = updates.collateralDescription;
    if (updates.assetsValue !== undefined) updateData.assetsValue = updates.assetsValue;
    if (updates.equipmentValue !== undefined) updateData.equipmentValue = updates.equipmentValue;
    if (updates.repaymentPlan !== undefined) updateData.repaymentPlan = updates.repaymentPlan;
    if (updates.repaymentAbility !== undefined) updateData.repaymentAbility = updates.repaymentAbility;
    if (updates.revenueProjections !== undefined) updateData.revenueProjections = updates.revenueProjections;
    if (updates.references !== undefined) updateData.references = updates.references;
    if (updates.businessLicenses !== undefined) updateData.businessLicenses = updates.businessLicenses;
    if (updates.insuranceInfo !== undefined) updateData.insuranceInfo = updates.insuranceInfo;
    if (updates.bankReferences !== undefined) updateData.bankReferences = updates.bankReferences;
    if (updates.applicationNumber !== undefined) updateData.applicationNumber = updates.applicationNumber;
    if (updates.lenderContactName !== undefined) updateData.lenderContactName = updates.lenderContactName;
    if (updates.lenderContactEmail !== undefined) updateData.lenderContactEmail = updates.lenderContactEmail;
    if (updates.lenderContactPhone !== undefined) updateData.lenderContactPhone = updates.lenderContactPhone;
    if (updates.lenderApplicationUrl !== undefined) updateData.lenderApplicationUrl = updates.lenderApplicationUrl;
    if (updates.requiredDocuments !== undefined) updateData.requiredDocuments = updates.requiredDocuments;
    if (updates.documentsSubmitted !== undefined) updateData.documentsSubmitted = updates.documentsSubmitted;
    if (updates.notes !== undefined) updateData.notes = updates.notes;
    if (updates.expectedDecisionDate !== undefined) updateData.expectedDecisionDate = this.toTimestamp(updates.expectedDecisionDate);
    if (updates.nextFollowUpDate !== undefined) updateData.nextFollowUpDate = this.toTimestamp(updates.nextFollowUpDate);

    await updateDoc(docRef, updateData);
  }

  /**
   * Update application status
   */
  static async updateStatus(
    id: string,
    status: FundingApplicationStatus,
    userId: string,
    additionalData?: {
      approvedAmount?: number;
      fundedAmount?: number;
      decisionDate?: Date;
      fundingDate?: Date;
      submissionDate?: Date;
      lenderNotes?: string;
    }
  ): Promise<void> {
    const docRef = doc(db, COLLECTION_NAME, id);
    const updateData: any = {
      status,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    };

    if (status === FundingApplicationStatus.SUBMITTED && additionalData?.submissionDate) {
      updateData.submissionDate = this.toTimestamp(additionalData.submissionDate);
    }

    if (status === FundingApplicationStatus.APPROVED && additionalData?.approvedAmount) {
      updateData.approvedAmount = additionalData.approvedAmount;
      updateData.decisionDate = this.toTimestamp(additionalData.decisionDate || new Date());
    }

    if (status === FundingApplicationStatus.FUNDED && additionalData?.fundedAmount) {
      updateData.fundedAmount = additionalData.fundedAmount;
      updateData.fundingDate = this.toTimestamp(additionalData.fundingDate || new Date());
    }

    if (status === FundingApplicationStatus.REJECTED && additionalData?.decisionDate) {
      updateData.decisionDate = this.toTimestamp(additionalData.decisionDate);
    }

    if (additionalData?.lenderNotes) {
      updateData.lenderNotes = additionalData.lenderNotes;
    }

    await updateDoc(docRef, updateData);
  }

  /**
   * Delete a funding application
   */
  static async delete(id: string): Promise<void> {
    const docRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(docRef);
  }

  /**
   * Get applications by status
   */
  static async getByStatus(status: FundingApplicationStatus): Promise<FundingApplication[]> {
    return this.getAll({ status });
  }

  /**
   * Get applications by program
   */
  static async getByProgram(program: FundingProgram): Promise<FundingApplication[]> {
    return this.getAll({ program });
  }
}

